import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import 'react-toastify/dist/ReactToastify.css';
import './Collector.css';

const url = process.env.REACT_APP_BACKEND_URL;

const Collector = () => {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [zones, setZones] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loadingZones, setLoadingZones] = useState(true);
  const [loadingDistricts, setLoadingDistricts] = useState(true);

  const [collector, setCollector] = useState({
    fname: '',
    mname: '',
    lname: '',
    mobile: '',
    username: '',
    password: '',
    zonename: '',
    districtname: '',
    status: 'active',
    photo: null
  });

  // Check if user is logged in and has company
  useEffect(() => {
    if (!authLoading && !user) {
      toast.error('Please login first');
      navigate('/login');
      return;
    }

    if (user && !user.company) {
      toast.error('No company assigned to your account');
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  // Fetch Zones with auth header
  useEffect(() => {
    if (!token || !user) return;
    
    axios.get(`${url}/api/zone`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        const data = Array.isArray(res.data?.data) ? res.data.data : [];
        setZones(data);
      })
      .catch(err => {
        console.error('Failed to load zones:', err);
        toast.error('Failed to load zones');
      })
      .finally(() => setLoadingZones(false));
  }, [token, user]);

  // Fetch Districts with auth header
  useEffect(() => {
    if (!token || !user) return;
    
    axios.get(`${url}/api/district`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        const data = Array.isArray(res.data?.data) ? res.data.data : [];
        setDistricts(data);
      })
      .catch(err => {
        console.error('Failed to load districts:', err);
        toast.error('Failed to load districts');
      })
      .finally(() => setLoadingDistricts(false));
  }, [token, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCollector(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Only JPEG, JPG, or PNG images are allowed');
        return;
      }
      
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      
      setCollector(prev => ({ ...prev, photo: file }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const requiredFields = ['fname', 'mname', 'lname', 'mobile', 'username', 'password', 'zonename', 'districtname', 'photo'];
    for (const field of requiredFields) {
      if (!collector[field]) {
        toast.error(`${field.replace(/([A-Z])/g, ' $1').trim()} is required`);
        return;
      }
    }
  
    // Validate mobile number
    if (!/^\d{9,15}$/.test(collector.mobile.replace(/\D/g, ''))) {
      toast.error('Please enter a valid mobile number');
      return;
    }
  
    // Validate username (minimum 3 characters)
    if (collector.username.length < 3) {
      toast.error('Username must be at least 3 characters long');
      return;
    }
  
    // Validate password (minimum 6 characters)
    if (collector.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }
  
    try {
      setSubmitting(true);
      const formData = new FormData();
      
      // Append all form fields - send password as plain text
      Object.entries(collector).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          if (key === 'photo' && value instanceof File) {
            formData.append('photo', value);
          } else {
            formData.append(key, value); // Password sent as plain text
          }
        }
      });
  
      console.log('Submitting collector data:', {
        ...collector,
        password: '***PLAIN TEXT PASSWORD***', // Just for logging
        company: user?.company,
        createdBy: user?.id
      });
  
      // Make API request with auth token
      const res = await axios.post(`${url}/api/collectors`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
  
      if (res.data.success) {
        toast.success('Collector added successfully!');
        
        // Clear form
        setCollector({
          fname: '',
          mname: '',
          lname: '',
          mobile: '',
          username: '',
          password: '',
          zonename: '',
          districtname: '',
          status: 'active',
          photo: null
        });
        
        // Reset file input
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = '';
        
        // Navigate to view collectors after short delay
        setTimeout(() => {
          navigate('/ViewCollector');
        }, 1500);
      }
    } catch (err) {
      console.error('Error adding collector:', err);
      toast.error(err.response?.data?.message || 'Failed to add Collector');
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="form-wrapper">
      <ToastContainer position="top-right" />
      <div className="collector-form-container">
        <h2 className="form-title">Add New Collector</h2>
        
        {/* Display company info */}
        {user?.companyDetails && (
          <div className="company-info" style={{ 
            marginBottom: '20px', 
            padding: '15px', 
            background: '#f8f9fa', 
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '14px', color: '#495057' }}>🏢 Company:</span>
              <strong style={{ fontSize: '16px', color: '#2c3e50' }}>
                {user.companyDetails.companyname} 
                {user.companyDetails.acronym && ` (${user.companyDetails.acronym})`}
              </strong>
            </div>
            <div style={{ fontSize: '13px', color: '#6c757d', marginTop: '5px' }}>
              Created by: {user.name} ({user.email})
            </div>
          </div>
        )}

        <form className="collector-form" onSubmit={handleSubmit} encType="multipart/form-data">
          {/* Personal Information Section */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '16px', color: '#495057', marginBottom: '15px' }}>Personal Information</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
              {['fname', 'mname', 'lname'].map(field => (
                <div key={field} className="form-field">
                  <input
                    type="text"
                    name={field}
                    value={collector[field]}
                    onChange={handleChange}
                    required
                  />
                  <label>{field === 'fname' ? 'First Name' : field === 'mname' ? 'Middle Name' : 'Last Name'}</label>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Information Section */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '16px', color: '#495057', marginBottom: '15px' }}>Contact Information</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
              <div className="form-field">
                <input
                  type="text"
                  name="mobile"
                  value={collector.mobile}
                  onChange={handleChange}
                  required
                />
                <label>Mobile Number</label>
              </div>
              <div className="form-field">
                <input
                  type="text"
                  name="username"
                  value={collector.username}
                  onChange={handleChange}
                  required
                />
                <label>Username</label>
              </div>
              <div className="form-field">
                <input
                  type="password"
                  name="password"
                  value={collector.password}
                  onChange={handleChange}
                  required
                />
                <label>Password</label>
              </div>
            </div>
          </div>

          {/* Location Section */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '16px', color: '#495057', marginBottom: '15px' }}>Location</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
              {/* Zone */}
              <div className="form-field">
                <select
                  name="zonename"
                  value={collector.zonename}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Zone</option>
                  {loadingZones ? (
                    <option disabled>Loading zones...</option>
                  ) : (
                    zones.map(z => <option key={z._id} value={z._id}>{z.name}</option>)
                  )}
                </select>
                <label>Zone</label>
              </div>

              {/* District */}
              <div className="form-field">
                <select
                  name="districtname"
                  value={collector.districtname}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select District</option>
                  {loadingDistricts ? (
                    <option disabled>Loading districts...</option>
                  ) : (
                    districts.map(d => <option key={d._id} value={d._id}>{d.name}</option>)
                  )}
                </select>
                <label>District</label>
              </div>
            </div>
          </div>

          {/* Status and Photo Section */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '16px', color: '#495057', marginBottom: '15px' }}>Status & Photo</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
              {/* Status */}
              <div className="form-field">
                <select name="status" value={collector.status} onChange={handleChange}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                <label>Status</label>
              </div>

              {/* Photo */}
              <div className="form-field">
                <input 
                  type="file" 
                  name="photo" 
                  id="photo-input"
                  onChange={handleFileChange} 
                  accept="image/jpeg,image/jpg,image/png"
                  required 
                />
                <label>Photo</label>
              </div>
            </div>
            
            {/* Photo Preview */}
            {collector.photo instanceof File && (
              <div style={{ marginTop: '15px', textAlign: 'center' }}>
                <img 
                  src={URL.createObjectURL(collector.photo)} 
                  alt="Preview" 
                  width="120" 
                  height="120"
                  style={{ 
                    borderRadius: '50%', 
                    objectFit: 'cover',
                    border: '3px solid #007bff'
                  }}
                />
                <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>{collector.photo.name}</p>
              </div>
            )}
          </div>

          <button type="submit" className="submit-btn" disabled={submitting}>
            {submitting ? 'Processing...' : 'Add Collector'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Collector;